export const PROPERTY_CREATION_STEP_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

// `shortLabel` is what the illustrated step cards show under their icon;
// `liveMessages` rotate in the status line while that step is in progress;
// `estimatedMs` paces the simulated progress (the backend does all four in one
// request): the DB insert is quick, the three LLM steps are not, and the CMA
// and marketing steps are the slowest. `weight` is each step's share of the
// percentage shown in the modal header, so the slow steps count for more.
export const DEFAULT_VOW_PROPERTY_CREATION_STEPS = [
  {
    id: 'create_property',
    label: 'Create property',
    shortLabel: 'Create property',
    estimatedMs: 1500,
    weight: 1,
    description: 'Creating your new listing from the selected MLS snapshot.',
    liveMessages: [
      'Reading the MLS snapshot…',
      'Copying rooms, features and photos…',
      'Saving the new listing…',
    ],
  },
  {
    id: 'generate_public_remarks',
    label: 'Generate public remarks',
    shortLabel: 'Public remarks',
    estimatedMs: 7000,
    weight: 2,
    description: 'Preparing listing description text for your property page.',
    liveMessages: [
      'Writing your listing remarks…',
      'Polishing the tone for buyers…',
    ],
  },
  {
    id: 'generate_cma_report',
    label: 'Generate CMA report',
    shortLabel: 'CMA report',
    estimatedMs: 14000,
    weight: 3,
    description: 'Building your comparative market analysis report.',
    liveMessages: [
      'Finding comparable recent sales…',
      'Estimating a suggested list price…',
    ],
  },
  {
    id: 'generate_marketing_strategy',
    label: 'Generate marketing strategy',
    shortLabel: 'Marketing strategy',
    estimatedMs: 14000,
    weight: 3,
    description: 'Creating a marketing strategy for your property.',
    liveMessages: [
      'Profiling likely buyers…',
      'Drafting channels and timing…',
    ],
  },
];

export const buildSequentialStepStatuses = (
  steps,
  activeIndex = 0,
  currentStatus = PROPERTY_CREATION_STEP_STATUS.IN_PROGRESS
) => {
  const normalizedSteps = Array.isArray(steps) ? steps : [];
  if (!normalizedSteps.length) {
    return {};
  }

  const safeIndex = Math.max(0, Math.min(activeIndex, normalizedSteps.length - 1));

  return normalizedSteps.reduce((acc, step, index) => {
    if (!step?.id) {
      return acc;
    }

    if (index < safeIndex) {
      acc[step.id] = PROPERTY_CREATION_STEP_STATUS.COMPLETED;
      return acc;
    }

    if (index === safeIndex) {
      acc[step.id] = currentStatus;
      return acc;
    }

    acc[step.id] = PROPERTY_CREATION_STEP_STATUS.PENDING;
    return acc;
  }, {});
};

/**
 * Build final step statuses from a property-onboarding/outreach response.
 * Shared by the "add property from MLS listing" and "outreach signup" flows so
 * both render the identical results modal.
 */
export const buildResultStepStatuses = (response) => {
  const seededId =
    response?.seeded_property?.id || response?.id || response?.property_id;
  const created =
    response?.property_created === true ||
    (response?.success !== false && Boolean(seededId));

  const { COMPLETED, FAILED, PENDING } = PROPERTY_CREATION_STEP_STATUS;

  return {
    create_property: created ? COMPLETED : FAILED,
    generate_public_remarks: created
      ? (response?.public_remark_generated ? COMPLETED : FAILED)
      : PENDING,
    generate_cma_report: created
      ? (response?.cma_generated?.success ? COMPLETED : FAILED)
      : PENDING,
    generate_marketing_strategy: created
      ? (response?.marketing_strategy_generated?.success ? COMPLETED : FAILED)
      : PENDING,
  };
};